'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Project } from '@inno-ck/shared';
import { useTr, t, useAutosave } from '@/shell/hooks';
import { patchProject } from '@/shell/services/api.service';
import type { MindmapCanvasProps, MindmapInstance, ConceptNode, KnowledgeCard, SpineCard, MindmapLevel, MindmapData, CtxMenuOption } from '@/types';

// ── Internal Types ─────────────────────────────────────────────────────────────

interface LayoutNode {
  id: string;
  label: string;
  color: string;
  imageUrl: string;
  x: number;
  y: number;
  collapsed: boolean;
  hasChildren: boolean;
  isTop: boolean;
  parentId: string | null;
  levelIdx?: number;
}

interface LayoutSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface KnowledgeBox extends KnowledgeCard {
  dashed: boolean;
  _type: 'existing' | 'toAcquire';
  x: number;
  y: number;
}

interface DragState {
  id: string;
  svgX: number;
  svgY: number;
  startX: number;
  startY: number;
  started: boolean;
}

interface SpineDragState {
  li: number;
  svgX: number;
  svgY: number;
  startX: number;
  startY: number;
  started: boolean;
}

interface DropHint {
  kind: 'child' | 'reorder';
  levelIdx: number;
  targetId?: string;
  parentId?: string | null;
  index?: number;
  x?: number;
  y?: number;
  lineX?: number;
  lineY?: number;
}

interface SpineDropHint {
  insertBefore: number;
  lineY: number;
}

interface DraggingCardState {
  id: string;
  li: number;
  origX: number;
  origY: number;
  origDx: number;
  origDy: number;
  startSvgX: number;
  startSvgY: number;
  liveX: number;
  liveY: number;
  moved: boolean;
}

interface DraggingLegendState {
  startSvgX: number;
  startSvgY: number;
  origX: number;
  origY: number;
}

interface TooltipState {
  text: string;
  x: number;
  y: number;
}

interface EditingCardState {
  li: number;
  id: string;
}

interface ImageModalState {
  li: number;
  nodeId: string;
  url: string;
}

interface CtxMenuState {
  x: number;
  y: number;
  options: CtxMenuOption[];
}

interface TransformState {
  x: number;
  y: number;
  scale: number;
}

interface NodeLabelProps {
  x: number;
  y: number;
  value: string;
  imageUrl: string;
  onSave: (value: string) => void;
  onImageClick: (url: string) => void;
}

interface InlineLabelProps {
  x: number;
  y: number;
  value: string;
  onSave: (value: string) => void;
  w?: number;
}

interface TitleBoxProps {
  x: number;
  y: number;
  w: number;
  h: number;
  value: string;
  onSave: (value: string) => void;
}

interface SpineLabelProps {
  spineX: number;
  spineY: number;
  value: string;
  onSave: (value: string) => void;
}

interface LegendLabelProps {
  x: number;
  y: number;
  w: number;
  value: string;
  onSave: (value: string) => void;
}

interface KnowledgeCardProps {
  box: KnowledgeBox;
  onEdit: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onShowTooltip: (text: string, x: number, y: number) => void;
  onHideTooltip: () => void;
  borderColor?: string;
  onCardDragStart: (e: React.MouseEvent) => void;
  onImageClick: (url: string) => void;
}

interface KnowledgeCardEditorProps {
  card: KnowledgeCard;
  onSave: (updates: Partial<KnowledgeCard>) => void;
  onClose: () => void;
  showBorderStyle?: boolean;
}

interface CtxMenuProps {
  menu: CtxMenuState | null;
  onClose: () => void;
}

interface NodeImageModalProps {
  modal: ImageModalState;
  onConfirm: (url: string) => void;
  onClose: () => void;
}

interface ConceptMapDiagramProps {
  data: MindmapData;
  onChange: (data: MindmapData) => void;
  svgElRef: React.MutableRefObject<SVGElement | null>;
}

interface ZoomBtnProps {
  label: string;
  onClick: () => void;
  wide?: boolean;
}

interface ConceptMapInstanceProps {
  project: Project;
  onInstanceReady: (instance: MindmapInstance) => void;
}

// ── helpers ──────────────────────────────────────────────────────────────────
const uid = (): string => Math.random().toString(36).slice(2, 9);
const newNode = (label = ''): ConceptNode => ({ id: uid(), label, children: [], collapsed: false, imageUrl: '' });
const newKNode = (): KnowledgeCard => ({ id: uid(), title: '', color: 'green', text: '', imageUrl: '', link: '', dx: 0, dy: 0 });
const newSpineCard = (): SpineCard => ({ id: uid(), title: '', color: 'green', text: '', imageUrl: '', link: '' });
// Normalize legacy string spineLabel → card object
const toSpineCard = (sl: string | SpineCard): SpineCard => (typeof sl === 'string' ? { ...newSpineCard(), title: sl } : (sl || newSpineCard()));

const findNode = (nodes: ConceptNode[], id: string): ConceptNode | null => {
  for (const n of nodes) {
    if (n.id === id) return n;
    const f = findNode(n.children ?? [], id);
    if (f) return f;
  }
  return null;
};

const deepCloneNode = (node: ConceptNode): ConceptNode => {
  return { ...node, id: uid(), children: (node.children ?? []).map(deepCloneNode) };
};

const makeDefaultData = (): MindmapData => {
  return {
    title: '',
    legendLabels: { green: '', yellow: '', red: '' },
    levels: [
      {
        id: uid(), spineLabel: newSpineCard(),
        concepts: [{ ...newNode(), children: [newNode(), newNode()] }],
        knowledge: { existing: [newKNode()], toAcquire: [newKNode()] },
      },
      {
        id: uid(), spineLabel: newSpineCard(),
        concepts: [{ ...newNode(), children: [newNode(), newNode()] }],
        knowledge: { existing: [newKNode()], toAcquire: [newKNode()] },
      },
    ],
  };
};

// ── Layout constants ──────────────────────────────────────────────────────────
const MARGIN_TOP    = 150;
const LEVEL_STEP    = 265;
const SPINE_X0      = 215;
const SPINE_DX      = -12;
const STEM_W        = 50;    // horizontal offset: spine dot → first concept
const CONCEPT_DROP  = 55;    // concepts hang this many px BELOW spine dot
const CONCEPT_GAP   = 205;   // horizontal gap between sibling concepts
const NODE_R        = 12;
const SPINE_R       = 9;
const CHILD_DY      = 92;
const CHILD_GAP     = 200;
const NODE_LABEL_W  = 165;
const NODE_LINE_H   = 17;
const SPINE_LABEL_W = 155;
const DIVIDER_X     = 750;
const KNOW_X0       = 780;
const KNOW_W        = 115;
const KNOW_H        = 170;
const KNOW_HEADER_H = 24;
const KNOW_GAP      = 14;
const KNOW_SEC_GAP  = 24;
const KNOW_COLOR: Record<string, string> = { green: '#3a7d44', yellow: '#e0a800', red: '#c0392b' };
// Brighter colours used specifically for concept-node circle fills
const NODE_COLORS: Record<string, string> = { green: '#22c55e', yellow: '#f59e0b', red: '#ef4444' };
const SVG_W         = 1240;

const spineAt = (li: number): { x: number; y: number } => {
  return { x: SPINE_X0 + li * SPINE_DX, y: MARGIN_TOP + li * LEVEL_STEP };
};

// ── Text wrap ─────────────────────────────────────────────────────────────────
const wrapText = (text: string, maxChars = 20): string[] => {
  if (!text) return [];
  const words = text.split(' ');
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const c = cur ? cur + ' ' + w : w;
    if (c.length <= maxChars) { cur = c; }
    else { if (cur) lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [text];
};

// ── Layout: bus at spine Y, nodes hang DOWN — matches reference image ─────────
const layoutConcepts = (concepts: ConceptNode[], spineX: number, spineY: number): { nodes: LayoutNode[]; segs: LayoutSegment[] } => {
  const nodes: LayoutNode[] = [];
  const segs: LayoutSegment[] = [];
  if (!concepts.length) return { nodes, segs };

  const firstCX  = spineX + STEM_W;
  const conceptY = spineY + CONCEPT_DROP;          // nodes are BELOW the bus
  const lastCX   = firstCX + (concepts.length - 1) * CONCEPT_GAP;

  // horizontal bus at spine dot Y, going RIGHT
  segs.push({ x1: spineX, y1: spineY, x2: lastCX, y2: spineY });

  // minX: nothing ever goes left of the spine dot (+ a small margin)
  const minX = spineX + SPINE_R + 10;

  const addChildren = (pX: number, pY: number, children: ConceptNode[], pId: string) => {
    if (!children?.length) return;
    const midY    = pY + CHILD_DY / 2;
    const childY  = pY + CHILD_DY;
    const half    = ((children.length - 1) * CHILD_GAP) / 2;
    // clamp: never let the leftmost child cross the spine
    const startCX = Math.max(minX, pX - half);

    // vertical stub from parent bottom to mid bus
    segs.push({ x1: pX, y1: pY + NODE_R, x2: pX, y2: midY });
    // horizontal bus at mid (draw from startCX, not from pX)
    if (children.length > 1)
      segs.push({ x1: startCX, y1: midY, x2: startCX + (children.length - 1) * CHILD_GAP, y2: midY });
    // vertical drops to each child
    children.forEach((ch, j) => {
      const cx = startCX + j * CHILD_GAP;
      segs.push({ x1: cx, y1: midY, x2: cx, y2: childY - NODE_R });
      nodes.push({ id: ch.id, label: ch.label, color: ch.color || '', imageUrl: ch.imageUrl || '',
                   x: cx, y: childY, collapsed: !!ch.collapsed, hasChildren: !!ch.children?.length,
                   isTop: false, parentId: pId });
      if (!ch.collapsed) addChildren(cx, childY, ch.children ?? [], ch.id);
    });
  };

  concepts.forEach((c, i) => {
    const cx = firstCX + i * CONCEPT_GAP;
    // vertical drop from bus down to concept
    segs.push({ x1: cx, y1: spineY, x2: cx, y2: conceptY - NODE_R });
    nodes.push({ id: c.id, label: c.label, color: c.color || '', imageUrl: c.imageUrl || '',
                 x: cx, y: conceptY, collapsed: !!c.collapsed, hasChildren: !!c.children?.length,
                 isTop: true, parentId: null });
    if (!c.collapsed) addChildren(cx, conceptY, c.children ?? [], c.id);
  });

  return { nodes, segs };
};

const layoutKnowledge = (knowledge: MindmapLevel['knowledge'], centerY: number, knowX0: number): KnowledgeBox[] => {
  const boxes: KnowledgeBox[] = [];
  const existTW = knowledge.existing.length * (KNOW_W + KNOW_GAP) - KNOW_GAP;
  const acqX    = knowX0 + existTW + KNOW_SEC_GAP;

  knowledge.existing.forEach((k, i) =>
    boxes.push({ ...k, dashed: k.dashed ?? false, _type: 'existing',
                 x: knowX0 + i * (KNOW_W + KNOW_GAP) + (k.dx || 0),
                 y: centerY - KNOW_H / 2 + (k.dy || 0) }));
  knowledge.toAcquire.forEach((k, i) =>
    boxes.push({ ...k, dashed: k.dashed ?? true, _type: 'toAcquire',
                 x: acqX + i * (KNOW_W + KNOW_GAP) + (k.dx || 0),
                 y: centerY - KNOW_H / 2 + (k.dy || 0) }));
  return boxes;
};

// ── NodeLabel: editable text + optional image thumbnail ───────────────────────
const NODE_IMG_W   = Math.round((KNOW_W - 8)              * 0.4); // 43px  (−60%)
const NODE_IMG_H   = Math.round((KNOW_H - KNOW_HEADER_H - 4) * 0.4); // 57px  (−60%)
const NODE_IMG_GAP = 5;  // gap between text bottom and image top

const NodeLabel: React.FC<NodeLabelProps> = ({ x, y, value, imageUrl, onSave, onImageClick }) => {
  const tr = useTr();
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(value), [value]);

  const commit = () => { setEditing(false); if (draft !== value) onSave(draft); };

  if (editing) {
    return (
      <foreignObject x={x} y={y - 11} width={NODE_LABEL_W} height={24}>
        <input ref={inputRef} value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter')  inputRef.current?.blur();
            if (e.key === 'Escape') { setDraft(value); setEditing(false); }
          }}
          style={{ width: '100%', fontSize: 12, border: '1px solid #aaa',
            borderRadius: 3, outline: 'none', padding: '2px 5px',
            boxSizing: 'border-box', color: '#222', background: '#fff' }}
          autoFocus
        />
      </foreignObject>
    );
  }

  const isEmpty = !value?.trim();
  const lines   = isEmpty ? [t(tr.clickLabel)] : wrapText(value, 20);
  const totalH  = lines.length * NODE_LINE_H;
  const startY  = y - totalH / 2 + NODE_LINE_H / 2;
  // bottom of last text line (dominant-baseline="middle" → centre of each line)
  const textBottom = y + totalH / 2 + 2;
  const imgY = textBottom + NODE_IMG_GAP;

  return (
    <g>
      {/* ── editable text ── */}
      <g onClick={e => { e.stopPropagation(); setDraft(value); setEditing(true); }}
         style={{ cursor: 'text' }}>
        <rect x={x - 2} y={startY - NODE_LINE_H / 2 - 2}
          width={NODE_LABEL_W} height={totalH + 4} fill="transparent"
          stroke={isEmpty ? '#cce8f5' : 'none'} strokeDasharray={isEmpty ? '3 2' : 'none'}
          strokeWidth={1} rx={2}
        />
        {lines.map((line, i) => (
          <text key={i} x={x + 2} y={startY + i * NODE_LINE_H}
            textAnchor="start" dominantBaseline="middle"
            fontSize={13} fill={isEmpty ? '#aaa' : '#222'} fontWeight="400"
            style={{ userSelect: 'none', pointerEvents: 'none' }}>
            {line}
          </text>
        ))}
      </g>

      {/* ── image thumbnail via foreignObject ── */}
      {imageUrl && (
        <>
          <foreignObject x={x} y={imgY} width={NODE_IMG_W} height={NODE_IMG_H}>
            <img
              src={imageUrl}
              alt=""
              style={{
                width: '100%', height: '100%',
                objectFit: 'contain',
                display: 'block',
                borderRadius: 4,
                pointerEvents: 'none',
              }}
            />
          </foreignObject>
          {/* subtle border */}
          <rect x={x} y={imgY} width={NODE_IMG_W} height={NODE_IMG_H}
            fill="none" stroke="#c8e4f5" strokeWidth={1.5} rx={4} />
          {/* zoom hint — click opens lightbox for the image only */}
          <g onClick={e => { e.stopPropagation(); onImageClick?.(imageUrl); }}
             style={{ cursor: 'zoom-in' }}>
            <rect x={x + NODE_IMG_W - 14} y={imgY + NODE_IMG_H - 14}
              width={14} height={14} rx={3} fill="rgba(0,0,0,0.55)" />
            <text x={x + NODE_IMG_W - 7} y={imgY + NODE_IMG_H - 7}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={9} fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>⤢</text>
          </g>
        </>
      )}
    </g>
  );
};

// ── InlineLabel: centered editable text (knowledge boxes) ─────────────────────
const InlineLabel: React.FC<InlineLabelProps> = ({ x, y, value, onSave, w = 64 }) => {
  const tr = useTr();
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(value), [value]);

  const commit = () => { setEditing(false); if (draft !== value) onSave(draft); };

  if (editing) {
    return (
      <foreignObject x={x - w / 2} y={y - 9} width={w} height={18}>
        <input ref={inputRef} value={draft}
          onChange={e => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter')  inputRef.current?.blur();
            if (e.key === 'Escape') { setDraft(value); setEditing(false); }
          }}
          style={{ width: '100%', fontSize: 10, border: '1px solid #29ABE2',
            borderRadius: 2, outline: 'none', textAlign: 'center',
            padding: '1px 2px', boxSizing: 'border-box', background: '#fff' }}
          autoFocus
        />
      </foreignObject>
    );
  }

  const isEmpty = !value;
  return (
    <g onClick={e => { e.stopPropagation(); setDraft(value); setEditing(true); }}
       style={{ cursor: 'text' }}>
      <rect x={x - w / 2} y={y - 8} width={w} height={16} fill="transparent"
        stroke={isEmpty ? '#c8e8f5' : 'none'}
        strokeWidth={isEmpty ? 1 : 0} strokeDasharray={isEmpty ? '3 2' : 'none'} rx={2}
      />
        <text x={x} y={y} textAnchor="middle" dominantBaseline="middle"
          fontSize={10} fill={isEmpty ? '#aaa' : '#444'}
          style={{ userSelect: 'none', pointerEvents: 'none' }}>
        {isEmpty ? t(tr.clickLabel) : value}
      </text>
    </g>
  );
};

// ── TitleBox ──────────────────────────────────────────────────────────────────
const TitleBox: React.FC<TitleBoxProps> = ({ x, y, w, h, value, onSave }) => {
  const tr = useTr();
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(value), [value]);

  const commit = () => { setEditing(false); onSave(draft.trim() || value); };

  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="#29ABE2" rx={4}
        style={{ cursor: editing ? 'default' : 'pointer' }}
        onClick={!editing ? () => { setDraft(value); setEditing(true); } : undefined}
      />
      {editing ? (
        <foreignObject x={x + 4} y={y + 4} width={w - 8} height={h - 8}>
          <input ref={inputRef} value={draft}
            onChange={e => setDraft(e.target.value)} onBlur={commit}
            onKeyDown={e => {
              if (e.key === 'Enter')  inputRef.current?.blur();
              if (e.key === 'Escape') { setDraft(value); setEditing(false); }
            }}
            style={{ width: '100%', height: '100%', background: 'transparent',
              border: 'none', outline: 'none', color: '#fff', fontWeight: 'bold',
              fontSize: 11, textAlign: 'center', cursor: 'text', boxSizing: 'border-box' }}
            autoFocus
          />
        </foreignObject>
      ) : (
        <>
          <text x={x + w / 2} y={y + h / 2}
            textAnchor="middle" dominantBaseline="middle"
            fontSize={11} fontWeight="bold" fill="#fff" letterSpacing="0.5"
            style={{ cursor: 'pointer', userSelect: 'none', pointerEvents: 'none' }}>
            {value || t(tr.examinedObject)}
          </text>
          <text x={x + w - 10} y={y + 8} fontSize={9} fill="rgba(255,255,255,0.55)"
            style={{ pointerEvents: 'none' }}>✎</text>
        </>
      )}
    </g>
  );
};

// ── SpineLabel ────────────────────────────────────────────────────────────────
const SpineLabel: React.FC<SpineLabelProps> = ({ spineX, spineY, value, onSave }) => {
  const tr = useTr();
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);
  const taRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setDraft(value), [value]);

  const boxX  = spineX - SPINE_LABEL_W - 18;
  const lines = (value || '').split('\n').slice(0, 6);
  const boxH  = Math.max(48, lines.length * 16 + 12);
  const commit = () => { setEditing(false); if (draft !== value) onSave(draft); };

  if (editing) {
    return (
      <foreignObject x={boxX} y={spineY - boxH / 2} width={SPINE_LABEL_W} height={boxH + 8}>
        <textarea ref={taRef} value={draft}
          onChange={e => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={e => { if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
          placeholder={t(tr.clickDescribe)}
          style={{ width: '100%', height: '100%', resize: 'none', fontSize: 12,
            lineHeight: '1.4', color: '#29ABE2', border: '1px solid #29ABE2',
            borderRadius: 4, outline: 'none', padding: '4px 6px',
            boxSizing: 'border-box', background: '#f0f9fe', fontFamily: 'inherit' }}
          autoFocus
        />
      </foreignObject>
    );
  }

  const isEmpty = !value?.trim();
  return (
    <g onClick={() => { setDraft(value); setEditing(true); }} style={{ cursor: 'text' }}>
      <rect x={boxX} y={spineY - boxH / 2} width={SPINE_LABEL_W} height={boxH}
        fill="transparent" stroke={isEmpty ? '#c8e8f5' : 'none'}
        strokeDasharray={isEmpty ? '4 3' : 'none'} strokeWidth={1} rx={4}
      />
      {isEmpty ? (
          <text x={boxX + SPINE_LABEL_W / 2} y={spineY}
            textAnchor="middle" dominantBaseline="middle"
            fontSize={11} fill="#b0d8f0"
            style={{ userSelect: 'none', pointerEvents: 'none' }}>
          {t(tr.clickDescribe)}
        </text>
      ) : lines.map((line, i) => {
        const totalH = lines.length * 16;
        const sy     = spineY - totalH / 2 + 8;
        return (
          <text key={i} x={boxX + SPINE_LABEL_W} y={sy + i * 16}
            textAnchor="end" dominantBaseline="middle"
            fontSize={12} fill="#29ABE2" fontWeight="500"
            style={{ userSelect: 'none', pointerEvents: 'none' }}>
            {line}
          </text>
        );
      })}
    </g>
  );
};

// ── LegendLabel: editable inline text for the legend ─────────────────────────
const LegendLabel: React.FC<LegendLabelProps> = ({ x, y, w, value, onSave }) => {
  const tr = useTr();
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { setEditing(false); if (draft !== value) onSave(draft); };

  if (editing) {
    return (
      <foreignObject x={x} y={y - 9} width={w} height={20}>
        <input ref={inputRef} value={draft}
          onChange={e => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter')  inputRef.current?.blur();
            if (e.key === 'Escape') { setDraft(value); setEditing(false); }
          }}
          style={{ width: '100%', fontSize: 10, border: '1px solid #29ABE2',
            borderRadius: 3, outline: 'none', padding: '1px 5px',
            boxSizing: 'border-box', background: '#fff', fontFamily: 'inherit', color: '#333' }}
          autoFocus
        />
      </foreignObject>
    );
  }

  const isEmpty = !value?.trim();
  return (
    <g onClick={e => { e.stopPropagation(); setDraft(value); setEditing(true); }}
       style={{ cursor: 'text' }}>
      <rect x={x} y={y - 9} width={w} height={18} rx={3}
        fill={isEmpty ? '#f4f4f4' : 'transparent'}
        stroke={isEmpty ? '#ddd' : 'none'} strokeWidth={1} />
        <text x={x + 4} y={y} textAnchor="start" dominantBaseline="middle"
        fontSize={10} fill={isEmpty ? '#bbb' : '#333'}
        style={{ userSelect: 'none', pointerEvents: 'none' }}>
        {isEmpty ? t(tr.legendDesc) : value}
      </text>
    </g>
  );
};

// ── KnowledgeCard (SVG card) ──────────────────────────────────────────────────
const KnowledgeCardComponent: React.FC<KnowledgeCardProps> = ({ box, onEdit, onContextMenu, onShowTooltip, onHideTooltip, borderColor, onCardDragStart, onImageClick }) => {
  const tr = useTr();
  const headerColor = KNOW_COLOR[box.color] || KNOW_COLOR.green;
  const title    = box.title ?? (box as any).label ?? '';
  // backward compat: old data stored image in content when contentType==='image'
  const imageUrl = box.imageUrl || ((box as any).contentType === 'image' ? (box as any).content : '') || '';
  const text     = box.text     || ((box as any).contentType !== 'image' ? (box as any).content : '') || '';
  const link     = box.link || '';
  const textTooLong = text.length > 85;

  const footerH  = link ? 22 : 0;
  const bodyY    = box.y + KNOW_HEADER_H + 4;
  const bodyH    = KNOW_H - KNOW_HEADER_H - 4 - footerH;

  // Split body: text on top, image below (proportional to what exists)
  const hasImage = !!imageUrl;
  const hasText  = !!text;
  const txtH     = hasText && hasImage ? Math.round(bodyH * 0.42) : hasText ? bodyH : 0;
  const imgY     = bodyY + txtH + (hasImage && hasText ? 3 : 0);
  const imgH     = bodyH - txtH - (hasImage && hasText ? 3 : 0);

  return (
    <g onContextMenu={onContextMenu}
       onMouseDown={e => { if (e.button === 0) { e.stopPropagation(); onCardDragStart?.(e); }}}
       onClick={e => { if (!onCardDragStart) { e.stopPropagation(); onEdit?.(e); } }}
       onMouseEnter={e => textTooLong && onShowTooltip?.(text, e.clientX, e.clientY)}
       onMouseMove={e  => textTooLong && onShowTooltip?.(text, e.clientX, e.clientY)}
       onMouseLeave={() => onHideTooltip?.()}
       style={{ cursor: 'grab' }}>
      {/* shadow */}
      <rect x={box.x + 2} y={box.y + 2} width={KNOW_W} height={KNOW_H} rx={6} fill="rgba(0,0,0,0.06)" />
      {/* card */}
      <rect x={box.x} y={box.y} width={KNOW_W} height={KNOW_H} rx={6}
        fill="#fff" stroke={borderColor || (box.dashed ? '#444' : '#222')} strokeWidth={borderColor ? 1.8 : 1.2}
        strokeDasharray={!borderColor && box.dashed ? '6 3' : undefined} />
      {/* header */}
      <clipPath id={`kclip-${box.id}`}>
        <rect x={box.x} y={box.y} width={KNOW_W} height={KNOW_HEADER_H + 6} rx={6} />
      </clipPath>
      <rect x={box.x} y={box.y} width={KNOW_W} height={KNOW_HEADER_H + 6}
        fill={headerColor} clipPath={`url(#kclip-${box.id})`} />
      <text x={box.x + KNOW_W / 2} y={box.y + KNOW_HEADER_H / 2 + 1}
        textAnchor="middle" dominantBaseline="middle"
        fontSize={9} fill="#fff" fontWeight="bold"
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {title ? (title.length > 15 ? title.slice(0, 14) + '…' : title) : `${t(tr.titleLabel)}…`}
      </text>

      {/* text zone — top */}
      {hasText && (
        <foreignObject x={box.x + 4} y={bodyY} width={KNOW_W - 8} height={txtH}>
          <div
            style={{ fontSize: 9, lineHeight: '1.35', color: '#444',
              overflow: 'hidden', height: '100%', wordBreak: 'break-word', userSelect: 'none' }}>
            {text}
          </div>
        </foreignObject>
      )}

      {/* image zone — below text */}
      {hasImage && (
        <>
          <image href={imageUrl}
            x={box.x + 4} y={imgY}
            width={KNOW_W - 8} height={imgH}
            preserveAspectRatio="xMidYMid meet"
            style={{ pointerEvents: 'none' }} />
          {/* zoom hint — click opens lightbox for the image only */}
          <g
            onMouseDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); onImageClick?.(imageUrl); }}
            style={{ cursor: 'zoom-in' }}>
            <rect x={box.x + KNOW_W - 16} y={imgY + imgH - 14}
              width={14} height={14} rx={3} fill="rgba(0,0,0,0.55)" />
            <text x={box.x + KNOW_W - 9} y={imgY + imgH - 7}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={9} fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>⤢</text>
          </g>
        </>
      )}

      {/* placeholder when empty */}
      {!hasImage && !hasText && (
        <foreignObject x={box.x + 4} y={bodyY} width={KNOW_W - 8} height={bodyH}>
          <div
            style={{ fontSize: 9, color: '#bbb', userSelect: 'none', paddingTop: 8, textAlign: 'center' }}>
            {t(tr.clickEdit)}
          </div>
        </foreignObject>
      )}

      {/* link footer */}
      {link && (
        <g onClick={e => { e.stopPropagation(); window.open(link, '_blank'); }} style={{ cursor: 'pointer' }}>
          <line x1={box.x + 4} y1={box.y + KNOW_H - 20} x2={box.x + KNOW_W - 4} y2={box.y + KNOW_H - 20}
            stroke="#eee" strokeWidth={1} />
          <text x={box.x + 6} y={box.y + KNOW_H - 8} fontSize={8} fill="#888" style={{ userSelect: 'none' }}>
            {'🔗 ' + (link.startsWith('data:') ? 'Document' : link.length > 18 ? link.slice(0, 17) + '…' : link)}
          </text>
        </g>
      )}

    </g>
  );
};

// ── KnowledgeCardEditor (HTML modal) ──────────────────────────────────────────
const KnowledgeCardEditor: React.FC<KnowledgeCardEditorProps> = ({ card, onSave, onClose, showBorderStyle = false }) => {
  const tr = useTr();
  const [title,    setTitle]    = useState(card.title ?? (card as any).label ?? '');
  const [color,    setColor]    = useState(card.color  || 'green');
  // backward compat
  const [text,     setText]     = useState(card.text     || ((card as any).contentType !== 'image' ? (card as any).content : '') || '');
  const [imageUrl, setImageUrl] = useState(card.imageUrl || ((card as any).contentType === 'image' ? (card as any).content : '') || '');
  const [link,     setLink]     = useState(card.link  || '');
  const [dashed,   setDashed]   = useState<boolean>(!!card.dashed);
  const imgRef = useRef<HTMLInputElement>(null);
  const docRef = useRef<HTMLInputElement>(null);

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader(); r.onload = ev => setImageUrl(ev.target?.result as string); r.readAsDataURL(file);
  };
  const handleDocFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader(); r.onload = ev => setLink(ev.target?.result as string); r.readAsDataURL(file);
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
      zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: 20, width: 340,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 13,
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#333' }}>{t(tr.editCard)}</div>

        {/* Color */}
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 5 }}>{t(tr.headerColor)}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {Object.entries(KNOW_COLOR).map(([key, hex]) => (
              <div key={key} onClick={() => setColor(key as 'green' | 'yellow' | 'red')} title={key}
                style={{ width: 26, height: 26, borderRadius: '50%', background: hex, cursor: 'pointer',
                  boxSizing: 'border-box', border: color === key ? '3px solid #222' : '3px solid transparent' }} />
            ))}
          </div>
        </div>

        {/* Border style — solid (existing) vs dashed (to acquire) */}
        {showBorderStyle && (
          <div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 5 }}>{t(tr.borderStyle)}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {([
                { val: false, title: t(tr.borderSolid),  dash: undefined as string | undefined },
                { val: true,  title: t(tr.borderDashed), dash: '5 3'    as string | undefined },
              ]).map(opt => (
                <div key={String(opt.val)} onClick={() => setDashed(opt.val)} title={opt.title}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    padding: '10px', borderRadius: 6, boxSizing: 'border-box',
                    border: dashed === opt.val ? '2px solid #29ABE2' : '1px solid #ddd',
                    background: dashed === opt.val ? '#f0f9fe' : '#fff' }}>
                  <svg width="46" height="18">
                    <rect x="2" y="2" width="42" height="14" rx="3" fill="none"
                      stroke="#333" strokeWidth="1.6" strokeDasharray={opt.dash} />
                  </svg>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Title */}
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{t(tr.titleLabel)}</div>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder={t(tr.titlePlaceholder)}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd',
              borderRadius: 5, fontSize: 12, boxSizing: 'border-box' }} />
        </div>

        {/* Text */}
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{t(tr.textLabel)}</div>
          <textarea value={text} onChange={e => setText(e.target.value)}
            placeholder={t(tr.textPlaceholder)} rows={3}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd',
              borderRadius: 5, fontSize: 11, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        {/* Image */}
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>{t(tr.imageLabel)}</div>
          <input value={imageUrl.startsWith('data:') ? '' : imageUrl} onChange={e => setImageUrl(e.target.value)}
            placeholder={t(tr.imageUrlPh)}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd',
              borderRadius: 5, fontSize: 11, boxSizing: 'border-box', marginBottom: 6 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: '#aaa' }}>{t(tr.orLabel)}</span>
            <button onClick={() => imgRef.current?.click()}
              style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11,
                border: '1px solid #ccc', background: '#f7f7f7', cursor: 'pointer' }}>
              {t(tr.fromComputer)}
            </button>
            <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageFile} />
            {imageUrl && (
              <button onClick={() => setImageUrl('')}
                style={{ padding: '4px 8px', borderRadius: 5, fontSize: 11,
                  border: '1px solid #fcc', background: '#fff5f5', cursor: 'pointer', color: '#c00' }}>
                ✕
              </button>
            )}
          </div>
          {imageUrl && (
            <img src={imageUrl} alt="" style={{ width: '100%', maxHeight: 100,
              objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }} />
          )}
        </div>

        {/* Link */}
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{t(tr.linkLabel)}</div>
          <input value={link.startsWith('data:') ? '' : link} onChange={e => setLink(e.target.value)}
            placeholder={t(tr.linkPh)}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd',
              borderRadius: 5, fontSize: 11, boxSizing: 'border-box', marginBottom: 6 }} />
          <button onClick={() => docRef.current?.click()}
            style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11,
              border: '1px solid #ccc', background: '#f7f7f7', cursor: 'pointer' }}>
            {t(tr.uploadDoc)}
          </button>
          {link.startsWith('data:') && (
            <span style={{ marginLeft: 8, fontSize: 10, color: '#3a7d44' }}>{t(tr.docUploaded)}</span>
          )}
          <input ref={docRef} type="file"
            accept=".pdf,.ppt,.pptx,.doc,.docx"
            style={{ display: 'none' }} onChange={handleDocFile} />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #ddd',
              background: '#f7f7f7', fontSize: 12, cursor: 'pointer', color: '#666' }}>
            {t(tr.cancelBtn)}
          </button>
          <button onClick={() => onSave({ title, color: color as 'green' | 'yellow' | 'red', text, imageUrl, link, ...(showBorderStyle ? { dashed } : {}) })}
            style={{ padding: '7px 16px', borderRadius: 6, border: 'none',
              background: '#29ABE2', fontSize: 12, cursor: 'pointer', color: '#fff', fontWeight: 600 }}>
            {t(tr.saveBtn)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Context menu (HTML overlay) ───────────────────────────────────────────────
const CtxMenu: React.FC<CtxMenuProps> = ({ menu, onClose }) => {
  useEffect(() => {
    if (!menu) return;
    // Dismiss on any click or new right-click elsewhere. The delay is generous
    // enough that the "click" synthesised when a long-press (touch) finger lifts
    // doesn't immediately re-close the menu it just opened.
    const h = () => onClose();
    const timer = setTimeout(() => {
      document.addEventListener('click', h);
      document.addEventListener('contextmenu', h);
    }, 350);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', h);
      document.removeEventListener('contextmenu', h);
    };
  }, [menu, onClose]);

  if (!menu) return null;

  // Use a Portal so the menu is appended to document.body, completely outside
  // any CSS-transformed ancestor (the pan/zoom canvas). Without this, position:fixed
  // would be relative to the transformed parent instead of the viewport.
  return createPortal(
    <div style={{
      position: 'fixed', left: menu.x, top: menu.y, zIndex: 9999,
      background: '#fff', border: '1px solid #ddd', borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 200, overflow: 'hidden',
    }}
      onClick={e => e.stopPropagation()}
      onContextMenu={e => e.stopPropagation()}
    >
      {menu.options.map((opt, i) => (
        opt.disabled
          ? <div key={i} style={{
              padding: '2px 12px', fontSize: 10, color: '#ddd',
              letterSpacing: '0.5px', userSelect: 'none', pointerEvents: 'none',
            }}>{opt.label}</div>
          : <div key={i}
              onClick={() => { opt.action(); onClose(); }}
              style={{
                padding: '9px 16px', cursor: 'pointer', fontSize: 13,
                color: opt.danger ? '#e53e3e' : '#222',
                borderBottom: i < menu.options.length - 1 ? '1px solid #f2f2f2' : 'none',
                fontWeight: opt.bold ? '600' : 'normal',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f7fafc'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {opt.label}
            </div>
      ))}
    </div>,
    document.body
  );
};

// ── Main diagram ──────────────────────────────────────────────────────────────
// ── NodeImageModal: pick image by URL or file upload ─────────────────────────
const NodeImageModal: React.FC<NodeImageModalProps> = ({ modal, onConfirm, onClose }) => {
  const tr = useTr();
  const [url, setUrl] = useState(modal.url || '');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setUrl(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: 'rgba(0,0,0,0.30)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 10,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        padding: '22px 24px 18px',
        width: 360, display: 'flex', flexDirection: 'column', gap: 12,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>{t(tr.nodeImageTitle)}</div>

        {/* URL input */}
        <input
          style={{
            width: '100%', border: '1px solid #bbb', borderRadius: 5,
            padding: '7px 10px', outline: 'none', boxSizing: 'border-box',
          }}
          placeholder={t(tr.nodeImageUrlPh)}
          value={url}
          onChange={e => setUrl(e.target.value)}
          onFocus={e => e.target.select()}
        />

        {/* File upload */}
        <button
          onClick={() => fileRef.current?.click()}
          style={{
            border: '1.5px dashed #b0d0e8', borderRadius: 6,
            background: '#f4faff', color: '#29ABE2', fontWeight: 600,
            padding: '8px 14px', cursor: 'pointer', fontSize: 13,
          }}>
          {t(tr.nodeImageUpload)}
        </button>
        <input ref={fileRef} type="file" accept="image/*"
          style={{ display: 'none' }} onChange={handleFile} />

        {/* Preview */}
        {url && (
          <div style={{ textAlign: 'center' }}>
            <img src={url} alt={t(tr.nodeImagePreview)}
              style={{
                maxWidth: '100%', maxHeight: 160,
                borderRadius: 6, border: '1px solid #e0e0e0',
                objectFit: 'contain',
              }}
            />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button onClick={onClose}
            style={{ background: '#eee', border: 'none', borderRadius: 5,
              padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#333' }}>
            {t(tr.cancel)}
          </button>
          <button
            onClick={() => onConfirm(url)}
            style={{ background: '#e05a00', border: 'none', borderRadius: 5,
              padding: '6px 16px', cursor: 'pointer', fontSize: 13,
              fontWeight: 600, color: '#fff' }}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const ConceptMapDiagram: React.FC<ConceptMapDiagramProps> = ({ data, onChange, svgElRef }) => {
  const tr = useTr();
  // ── All state declarations first (hooks must be in consistent order) ──────────
  const [ctxMenu,       setCtxMenu]       = useState<CtxMenuState | null>(null);
  const [dragging,      setDragging]      = useState<DragState | null>(null);
  const [dropHint,      setDropHint]      = useState<DropHint | null>(null);
  const [spineDragging, setSpineDragging] = useState<SpineDragState | null>(null);
  const [spineDropHint, setSpineDropHint] = useState<SpineDropHint | null>(null);
  const [editingCard,       setEditingCard]       = useState<EditingCardState | null>(null); // { li, id }
  const [editingSpineCard,  setEditingSpineCard]  = useState<number | null>(null); // li index
  const [tooltip,           setTooltip]           = useState<TooltipState | null>(null); // { text, x, y }
  const [draggingCard,      setDraggingCard]      = useState<DraggingCardState | null>(null); // { id, li, origX, origY, origDx, origDy, startSvgX, startSvgY, liveX, liveY, moved }
  const [legendPos,         setLegendPos]         = useState<{ x: number; y: number } | null>(null); // { x, y } — null = default top-right
  const [legendCollapsed,   setLegendCollapsed]   = useState(false);
  const [draggingLegend,    setDraggingLegend]    = useState<DraggingLegendState | null>(null); // { startSvgX, startSvgY, origX, origY }
  const [imageModal,        setImageModal]        = useState<ImageModalState | null>(null); // { li, nodeId, url }
  const [lightboxUrl,       setLightboxUrl]       = useState<string | null>(null);
  const internalSvgRef = useRef<SVGElement | null>(null);
  const svgRef = svgElRef ?? internalSvgRef;

  const n      = data.levels.length;
  const lastSp = spineAt(n - 1);
  const svgH   = lastSp.y + LEVEL_STEP + 80;

  // ── Dynamic divider + all layout nodes (with levelIdx + parentId) ────────────
  const allNodes = data.levels.flatMap((level, li) => {
    const sp = spineAt(li);
    return layoutConcepts(level.concepts, sp.x, sp.y).nodes.map(nd => ({ ...nd, levelIdx: li }));
  });
  const validNodes = allNodes.filter(nd => Number.isFinite(nd.x));
  const maxLabelRight = validNodes.length > 0
    ? Math.max(...validNodes.map(nd => nd.x + NODE_R + 7 + NODE_LABEL_W))
    : DIVIDER_X;
  const dividerX = Math.max(DIVIDER_X, maxLabelRight + 30);
  const knowX0   = dividerX + 30;

  // Rightmost extent of the knowledge cards across all levels, so the canvas
  // grows when post-its are added instead of clipping them. Mirrors layoutKnowledge.
  const knowRightEdge = (knowledge: MindmapLevel['knowledge']): number => {
    const existTW = knowledge.existing.length * (KNOW_W + KNOW_GAP) - KNOW_GAP;
    const acqX    = knowX0 + existTW + KNOW_SEC_GAP;
    let right = knowX0 + KNOW_W;
    knowledge.existing.forEach((k, i) => { right = Math.max(right, knowX0 + i * (KNOW_W + KNOW_GAP) + (k.dx || 0) + KNOW_W); });
    knowledge.toAcquire.forEach((k, i) => { right = Math.max(right, acqX + i * (KNOW_W + KNOW_GAP) + (k.dx || 0) + KNOW_W); });
    return right;
  };
  const maxKnowRight = Math.max(knowX0 + KNOW_W, ...data.levels.map(l => knowRightEdge(l.knowledge)));
  const LEGEND_W = 182;
  // reserve room on the right for the default (top-right) legend so it never overlaps cards
  const svgW = Math.max(knowX0 + 520, maxKnowRight + 30 + (legendPos ? 0 : LEGEND_W + 10));

  const mutate = useCallback((fn: (d: MindmapData) => MindmapData) => onChange(fn(JSON.parse(JSON.stringify(data)))), [data, onChange]);

  // ── Convert client coords → SVG coords ───────────────────────────────────────
  const toSVG = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const pt = (svgRef.current as any).createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const r = pt.matrixTransform((svgRef.current as any).getScreenCTM().inverse());
    return { x: r.x, y: r.y };
  }, [svgRef]);

  // ── Move node: remove from source, insert at target ──────────────────────────
  const moveNode = useCallback((nodeId: string, toLevelIdx: number, toParentId: string | null, toIndex: number) => mutate(d => {
    let movedNode: ConceptNode | null = null;
    const remove = (arr: ConceptNode[]): boolean => {
      const i = arr.findIndex(n => n.id === nodeId);
      if (i !== -1) { movedNode = arr.splice(i, 1)[0] ?? null; return true; }
      return arr.some(n => remove(n.children ?? []));
    };
    d.levels.forEach(lv => remove(lv.concepts));
    if (!movedNode) return d;

    if (toParentId === null) {
      const arr = d.levels[toLevelIdx]?.concepts;
      if (arr) arr.splice(Math.min(toIndex, arr.length), 0, movedNode);
    } else {
      const insert = (arr: ConceptNode[]): boolean => {
        for (const n of arr) {
          if (n.id === toParentId) {
            n.children = n.children ?? [];
            n.children.splice(Math.min(toIndex, n.children.length), 0, movedNode!);
            return true;
          }
          if (insert(n.children ?? [])) return true;
        }
        return false;
      };
      for (const lv of d.levels) insert(lv.concepts);
    }
    return d;
  }), [mutate]);

  // ── Compute drop hint from current SVG mouse position ────────────────────────
  const computeDropHint = useCallback((svgX: number, svgY: number, dragId: string): DropHint | null => {
    const others = allNodes.filter(nd => nd.id !== dragId);

    // 1) Near a node circle → make child
    let closest: LayoutNode | null = null, closestD = 30;
    for (const nd of others) {
      const d = Math.hypot(svgX - nd.x, svgY - nd.y);
      if (d < closestD) { closestD = d; closest = nd; }
    }
    if (closest) return { kind: 'child', levelIdx: closest.levelIdx!, targetId: closest.id, x: closest.x, y: closest.y };

    // 2) Near a sibling-group Y → reorder
    const groups: Record<string, { levelIdx: number; parentId: string | null; nodes: LayoutNode[] }> = {};
    for (const nd of others) {
      const key = `${nd.levelIdx}:${nd.parentId ?? ''}`;
      if (!groups[key]) groups[key] = { levelIdx: nd.levelIdx!, parentId: nd.parentId, nodes: [] };
      groups[key].nodes.push(nd);
    }
    let bestGrp: { levelIdx: number; parentId: string | null; nodes: LayoutNode[] } | null = null, bestDy = 28;
    for (const g of Object.values(groups)) {
      const dy = Math.abs(svgY - (g.nodes[0]?.y ?? 0));
      if (dy < bestDy) { bestDy = dy; bestGrp = g; }
    }
    if (bestGrp) {
      const sorted = [...bestGrp.nodes].sort((a, b) => a.x - b.x);
      let idx = sorted.length;
      for (let i = 0; i < sorted.length; i++) {
        const midX = i === 0 ? sorted[0]!.x - CONCEPT_GAP / 2
                              : (sorted[i - 1]!.x + sorted[i]!.x) / 2;
        if (svgX <= midX + (i === 0 ? CONCEPT_GAP / 2 : 0)) { idx = i; break; }
      }
      const lineX = sorted.length === 0 ? svgX
        : idx === 0              ? sorted[0]!.x - 24
        : idx === sorted.length  ? sorted[sorted.length - 1]!.x + 24
        : (sorted[idx - 1]!.x + sorted[idx]!.x) / 2;
      return { kind: 'reorder', levelIdx: bestGrp.levelIdx, parentId: bestGrp.parentId,
               index: idx, lineX, lineY: bestGrp.nodes[0]!.y };
    }
    return null;
  }, [allNodes]);

  // ── Spine drop hint (must be defined before onSVGMouseMove) ──────────────────
  const computeSpineDropHint = useCallback((svgY: number, dragLi: number): SpineDropHint | null => {
    const otherYs = data.levels
      .map((_, i) => i).filter(i => i !== dragLi)
      .map(i => spineAt(i).y);                   // sorted ascending

    if (!otherYs.length) return null;            // only 1 level, nothing to reorder

    let insertBefore = otherYs.length;
    for (let j = 0; j < otherYs.length; j++) {
      if (svgY < otherYs[j]!) { insertBefore = j; break; }
    }
    const lineY = insertBefore === 0             ? otherYs[0]! - LEVEL_STEP * 0.4
                : insertBefore === otherYs.length ? otherYs[otherYs.length - 1]! + LEVEL_STEP * 0.4
                : (otherYs[insertBefore - 1]! + otherYs[insertBefore]!) / 2;
    return { insertBefore, lineY };
  }, [data.levels]);

  const moveLevel = useCallback((fromIndex: number, insertBefore: number) => mutate(d => {
    const [moved] = d.levels.splice(fromIndex, 1);
    d.levels.splice(insertBefore, 0, moved!);
    return d;
  }), [mutate]);

  // ── Drag handlers ─────────────────────────────────────────────────────────────
  const startDrag = useCallback((e: React.MouseEvent, nodeId: string) => {
    e.preventDefault(); e.stopPropagation();
    const { x, y } = toSVG(e.clientX, e.clientY);
    setDragging({ id: nodeId, svgX: x, svgY: y, startX: x, startY: y, started: false });
  }, [toSVG]);

  const onSVGMouseMove = useCallback((e: React.MouseEvent) => {
    const { x, y } = toSVG(e.clientX, e.clientY);
    if (dragging) {
      const started = dragging.started || Math.hypot(x - dragging.startX, y - dragging.startY) > 6;
      setDragging(d => d ? { ...d, svgX: x, svgY: y, started } : null);
      if (started) setDropHint(computeDropHint(x, y, dragging.id));
    }
    if (spineDragging) {
      const started = spineDragging.started || Math.hypot(y - spineDragging.startY) > 6;
      setSpineDragging(d => d ? { ...d, svgX: x, svgY: y, started } : null);
      if (started) setSpineDropHint(computeSpineDropHint(y, spineDragging.li));
    }
    if (draggingCard) {
      const ddx = x - draggingCard.startSvgX;
      const ddy = y - draggingCard.startSvgY;
      const moved = draggingCard.moved || Math.hypot(ddx, ddy) > 5;
      setDraggingCard(d => d ? { ...d, liveX: d.origX + ddx, liveY: d.origY + ddy, moved } : null);
    }
    if (draggingLegend) {
      setLegendPos({
        x: draggingLegend.origX + (x - draggingLegend.startSvgX),
        y: draggingLegend.origY + (y - draggingLegend.startSvgY),
      });
    }
  }, [dragging, spineDragging, draggingCard, draggingLegend, toSVG, computeDropHint, computeSpineDropHint]);

  const onSVGMouseUp = useCallback(() => {
    if (dragging?.started && dropHint) {
      if (dropHint.kind === 'child') {
        moveNode(dragging.id, dropHint.levelIdx, dropHint.targetId!, 9999);
      } else if (dropHint.kind === 'reorder') {
        moveNode(dragging.id, dropHint.levelIdx, dropHint.parentId!, dropHint.index!);
      }
    }
    if (spineDragging?.started && spineDropHint) {
      moveLevel(spineDragging.li, spineDropHint.insertBefore);
    }
    if (draggingCard) {
      if (draggingCard.moved) {
        const ddx = draggingCard.liveX - draggingCard.origX;
        const ddy = draggingCard.liveY - draggingCard.origY;
        mutate(d => {
          const lv = d.levels[draggingCard.li]!;
          for (const arr of [lv.knowledge.existing, lv.knowledge.toAcquire]) {
            const k = arr.find(k => k.id === draggingCard.id);
            if (k) { k.dx = draggingCard.origDx + ddx; k.dy = draggingCard.origDy + ddy; break; }
          }
          return d;
        });
      } else {
        // simple click (no move) → open editor
        setEditingCard({ li: draggingCard.li, id: draggingCard.id });
      }
      setDraggingCard(null);
    }
    setDragging(null); setDropHint(null);
    setSpineDragging(null); setSpineDropHint(null);
    setDraggingLegend(null);
  }, [dragging, dropHint, moveNode, spineDragging, spineDropHint, moveLevel, draggingCard, mutate]);

  const cancelDrag = useCallback(() => {
    setDragging(null); setDropHint(null);
    setSpineDragging(null); setSpineDropHint(null);
    setDraggingCard(null);
    setDraggingLegend(null);
  }, []);

  const startSpineDrag = useCallback((e: React.MouseEvent, li: number) => {
    e.preventDefault(); e.stopPropagation();
    const { x, y } = toSVG(e.clientX, e.clientY);
    setSpineDragging({ li, svgX: x, svgY: y, startX: x, startY: y, started: false });
  }, [toSVG]);

  const startCardDrag = useCallback((e: React.MouseEvent, box: KnowledgeBox, li: number) => {
    e.preventDefault();
    const { x, y } = toSVG(e.clientX, e.clientY);
    setDraggingCard({
      id: box.id, li,
      origX: box.x, origY: box.y,
      origDx: box.dx || 0, origDy: box.dy || 0,
      startSvgX: x, startSvgY: y,
      liveX: box.x, liveY: box.y,
      moved: false,
    });
  }, [toSVG]);

  const startLegendDrag = useCallback((e: React.MouseEvent, currentX: number, currentY: number) => {
    e.preventDefault(); e.stopPropagation();
    const { x, y } = toSVG(e.clientX, e.clientY);
    setDraggingLegend({ startSvgX: x, startSvgY: y, origX: currentX, origY: currentY });
  }, [toSVG]);

  // ── mutations ──
  const setTitle = (t: string) => onChange({ ...data, title: t });
  const setLegendLabel = (color: string, text: string) => onChange({
    ...data,
    legendLabels: { ...(data.legendLabels || {}), [color]: text },
  });
  const updateSpineCard = (li: number, updates: Partial<SpineCard>) => mutate(d => {
    d.levels[li]!.spineLabel = { ...toSpineCard(d.levels[li]!.spineLabel), ...updates };
    return d;
  });
  const setNodeLabel = (li: number, id: string, l: string) => mutate(d => {
    const walk = (a: ConceptNode[]) => { for (const n of a) { if (n.id === id) { n.label = l; return; } walk(n.children ?? []); } };
    walk(d.levels[li]!.concepts); return d;
  });
  const setNodeColor = (li: number, id: string, color: 'green' | 'yellow' | 'red' | '') => mutate(d => {
    const walk = (a: ConceptNode[]) => { for (const n of a) { if (n.id === id) { n.color = color; return; } walk(n.children ?? []); } };
    walk(d.levels[li]!.concepts); return d;
  });
  const setNodeImage = (li: number, id: string, url: string) => mutate(d => {
    const walk = (a: ConceptNode[]) => { for (const n of a) { if (n.id === id) { n.imageUrl = url; return; } walk(n.children ?? []); } };
    walk(d.levels[li]!.concepts); return d;
  });
  const updateKNode = (li: number, id: string, updates: Partial<KnowledgeCard>) => mutate(d => {
    const lv = d.levels[li]!;
    for (const k of [...lv.knowledge.existing, ...lv.knowledge.toAcquire])
      if (k.id === id) { Object.assign(k, updates); break; }
    return d;
  });
  const duplicateKNode = (li: number, id: string) => mutate(d => {
    const lv = d.levels[li]!;
    for (const arr of [lv.knowledge.existing, lv.knowledge.toAcquire]) {
      const idx = arr.findIndex(k => k.id === id);
      if (idx !== -1) {
        const copy = { ...arr[idx]!, id: uid(), dx: (arr[idx]!.dx||0) + 20, dy: (arr[idx]!.dy||0) + 20 };
        arr.splice(idx + 1, 0, copy as KnowledgeCard);
        break;
      }
    }
    return d;
  });
  const toggleCollapse = (li: number, id: string) => mutate(d => {
    const walk = (a: ConceptNode[]) => { for (const n of a) { if (n.id === id) { n.collapsed = !n.collapsed; return; } walk(n.children ?? []); } };
    walk(d.levels[li]!.concepts); return d;
  });
  const removeNode = (li: number, id: string) => mutate(d => {
    const walk = (a: ConceptNode[]): boolean => {
      const i = a.findIndex(n => n.id === id);
      if (i !== -1) { a.splice(i, 1); return true; }
      for (const n of a) if (walk(n.children ?? [])) return true;
      return false;
    };
    walk(d.levels[li]!.concepts); return d;
  });
  const addConceptToLevel = (li: number) => mutate(d => { d.levels[li]!.concepts.push(newNode()); return d; });
  const duplicateConceptNode = (li: number, id: string) => mutate(d => {
    const walk = (a: ConceptNode[], parent: ConceptNode[] | null): boolean => {
      const i = a.findIndex(n => n.id === id);
      if (i !== -1) {
        const clone = deepCloneNode(a[i]!);
        a.splice(i + 1, 0, clone);
        return true;
      }
      for (const n of a) if (walk(n.children ?? [], a)) return true;
      return false;
    };
    walk(d.levels[li]!.concepts, null); return d;
  });

  // ── mutations: levels ──
  const addLevel = () => mutate(d => {
    d.levels.push({
      id: uid(), spineLabel: newSpineCard(),
      concepts: [newNode()],
      knowledge: { existing: [newKNode()], toAcquire: [newKNode()] },
    });
    return d;
  });
  const removeLevelAt = (idx: number) => mutate(d => { d.levels.splice(idx, 1); return d; });

  // ── mutations: knowledge cards ──
  const addKnowledge = (li: number, which: 'existing' | 'toAcquire') => mutate(d => {
    d.levels[li]!.knowledge[which].push(newKNode());
    return d;
  });
  const removeKnowledge = (li: number, id: string) => mutate(d => {
    const lv = d.levels[li]!;
    for (const arr of [lv.knowledge.existing, lv.knowledge.toAcquire] as KnowledgeCard[][]) {
      const i = arr.findIndex(k => k.id === id);
      if (i !== -1) { arr.splice(i, 1); break; }
    }
    return d;
  });

  // ── Context menus ──
  const addChildToNode = (li: number, id: string) => mutate(d => {
    const walk = (a: ConceptNode[]) => { for (const n of a) { if (n.id === id) { n.children = n.children ?? []; n.children.push(newNode()); return; } walk(n.children ?? []); } };
    walk(d.levels[li]!.concepts); return d;
  });
  // Long-press → open the context menu on touch devices (no right-click on tablets).
  // Mouse keeps using the native right-click (contextmenu) handler.
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressOrigin = useRef<{ x: number; y: number } | null>(null);
  const cancelLongPress = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    longPressOrigin.current = null;
  };
  const longPress = (open: (x: number, y: number) => void) => ({
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      const x = e.clientX, y = e.clientY;
      cancelLongPress();
      longPressOrigin.current = { x, y };
      longPressTimer.current = setTimeout(() => { longPressTimer.current = null; open(x, y); }, 450);
    },
    onPointerMove: (e: React.PointerEvent) => {
      const o = longPressOrigin.current;
      if (o && Math.hypot(e.clientX - o.x, e.clientY - o.y) > 10) cancelLongPress();
    },
    onPointerUp: cancelLongPress,
    onPointerCancel: cancelLongPress,
  });

  const openNodeMenu = (clientX: number, clientY: number, li: number, id: string, isTop: boolean) => {
    const node = findNode(data.levels[li]!.concepts, id);
    const opts: CtxMenuOption[] = [];
    if (node?.children?.length) {
      opts.push({ label: node.collapsed ? t(tr.expand) : t(tr.collapse), bold: true,
        action: () => toggleCollapse(li, id) });
    }
    opts.push({ label: t(tr.addChild),       action: () => addChildToNode(li, id) });
    opts.push({ label: t(tr.duplicateNode),   action: () => duplicateConceptNode(li, id) });
    if (isTop) opts.push({ label: t(tr.addConcept), action: () => addConceptToLevel(li) });
    opts.push({ label: '─────────────────', disabled: true, action: () => {} });
    opts.push({ label: t(tr.colorGreen),   action: () => setNodeColor(li, id, 'green') });
    opts.push({ label: t(tr.colorYellow),  action: () => setNodeColor(li, id, 'yellow') });
    opts.push({ label: t(tr.colorRed),     action: () => setNodeColor(li, id, 'red') });
    opts.push({ label: t(tr.colorDefault), action: () => setNodeColor(li, id, '') });
    opts.push({ label: '─────────────────', disabled: true, action: () => {} });
    opts.push({ label: t(tr.addNodeImage), action: () => setImageModal({ li, nodeId: id, url: node?.imageUrl || '' }) });
    if (node?.imageUrl) opts.push({ label: t(tr.removeNodeImage), action: () => setNodeImage(li, id, '') });
    opts.push({ label: '─────────────────', disabled: true, action: () => {} });
    if (!isTop) opts.push({ label: t(tr.deleteNode), danger: true, action: () => removeNode(li, id) });
    setCtxMenu({ x: clientX + 4, y: clientY + 4, options: opts });
  };

  const openSpineMenu = (clientX: number, clientY: number, li: number) => {
    const opts: CtxMenuOption[] = [
      { label: t(tr.addConcept), action: () => addConceptToLevel(li) },
      { label: t(tr.addLevel),   action: () => addLevel() },
    ];
    if (data.levels.length > 1)
      opts.push({ label: t(tr.deleteLevel), danger: true, action: () => removeLevelAt(li) });
    setCtxMenu({ x: clientX + 4, y: clientY + 4, options: opts });
  };

  const openKnowMenu = (clientX: number, clientY: number, li: number, kId: string) => {
    setCtxMenu({ x: clientX + 4, y: clientY + 4, options: [
      { label: t(tr.dupKnowledge),    action: () => duplicateKNode(li, kId) },
      { label: t(tr.addExisting),     action: () => addKnowledge(li, 'existing') },
      { label: t(tr.addToAcquire),    action: () => addKnowledge(li, 'toAcquire') },
      { label: t(tr.deleteKnowledge), danger: true, action: () => removeKnowledge(li, kId) },
    ]});
  };

  return (
    <>
    <svg ref={svgRef as React.RefObject<SVGSVGElement>} width={svgW} height={svgH}
      viewBox={`0 0 ${svgW} ${svgH}`}
      style={{ background: '#fff', display: 'block', cursor: (dragging?.started || draggingCard?.moved) ? 'grabbing' : 'default' }}
      xmlns="http://www.w3.org/2000/svg"
      onClick={() => setCtxMenu(null)}
      onContextMenu={e => e.preventDefault()}
      onMouseMove={onSVGMouseMove}
      onMouseUp={onSVGMouseUp}
      onMouseLeave={cancelDrag}
    >
      {/* ── Section headers ── */}
      <text x={390} y={32} textAnchor="middle" fontSize={13} fontWeight="bold"
        fill="#29ABE2" letterSpacing="1">{t(tr.conceptSpace)}</text>
      <text x={knowX0 + 200} y={32} textAnchor="middle" fontSize={13} fontWeight="bold"
        fill="#555" letterSpacing="1">{t(tr.knowledgeSpace)}</text>

      {/* ── enjeu.org logo — centered on divider ── */}
      <a href="https://enjeu.org" target="_blank" rel="noreferrer"
         style={{ cursor: 'pointer' }}>
        <image href="/logo2_enjeu-org.png"
          x={dividerX - 72} y={4} width={144} height={36}
          preserveAspectRatio="xMidYMid meet" />
      </a>

      {/* ── Divider ── */}
      <line x1={dividerX} y1={44} x2={dividerX} y2={svgH}
        stroke="#222" strokeWidth={1.5} strokeDasharray="6 5" />

      {/* ── Knowledge legend (draggable + collapsible) ── */}
      {(() => {
        const lw        = 182;
        const titleH    = 26;
        const labels    = data.legendLabels || { green: '', yellow: '', red: '' };
        const colorRows: [string, string, string][] = [
          ['green',  KNOW_COLOR.green!,  labels.green ?? ''  ],
          ['yellow', KNOW_COLOR.yellow!, labels.yellow ?? '' ],
          ['red',    KNOW_COLOR.red!,    labels.red ?? ''    ],
        ];
        const fullH  = titleH + 20 + 20 + 14 + colorRows.length * 28 + 12;
        const boxH   = legendCollapsed ? titleH : fullH;
        // position: use legendPos if set, otherwise default top-right
        const lx     = legendPos ? legendPos.x : svgW - lw - 10;
        const ly     = legendPos ? legendPos.y : 48;
        const isDraggingNow = !!draggingLegend;

        return (
          <g style={{ filter: isDraggingNow ? 'drop-shadow(0 4px 10px rgba(0,0,0,0.18))' : undefined }}>
            {/* background */}
            <rect x={lx} y={ly} width={lw} height={boxH} rx={7}
              fill="#fafafa" stroke="#d0d0d0" strokeWidth={1} />

            {/* ── title bar (drag handle) ── */}
            <rect x={lx} y={ly} width={lw} height={titleH} rx={7}
              fill={isDraggingNow ? '#ddeeff' : '#efefef'}
              style={{ cursor: isDraggingNow ? 'grabbing' : 'grab' }}
              onMouseDown={e => { if (e.button === 0) startLegendDrag(e, lx, ly); }}
            />
            {/* square off bottom-left + bottom-right corners of title when expanded */}
            {!legendCollapsed && (
              <rect x={lx} y={ly + titleH - 7} width={lw} height={7} fill={isDraggingNow ? '#ddeeff' : '#efefef'} />
            )}
            {/* title text */}
            <text x={lx + lw / 2} y={ly + 13} textAnchor="middle" dominantBaseline="middle"
              fontSize={10} fontWeight="bold" fill="#555" letterSpacing="0.5"
              style={{ userSelect: 'none', pointerEvents: 'none' }}>{t(tr.legendTitle)}</text>
            {/* collapse toggle button */}
            <g onClick={e => { e.stopPropagation(); setLegendCollapsed(c => !c); }}
               style={{ cursor: 'pointer' }}>
              <rect x={lx + lw - 24} y={ly + 5} width={18} height={16} rx={3}
                fill="rgba(0,0,0,0.05)" />
              <text x={lx + lw - 15} y={ly + 13} textAnchor="middle" dominantBaseline="middle"
                fontSize={10} fill="#777" style={{ userSelect: 'none', pointerEvents: 'none' }}>
                {legendCollapsed ? '＋' : '－'}
              </text>
            </g>

            {/* ── body — hidden when collapsed ── */}
            {!legendCollapsed && (
              <g>
                {/* solid line row */}
                <rect x={lx + 12} y={ly + titleH + 6} width={32} height={14} rx={2}
                  fill="none" stroke="#222" strokeWidth={1.2} />
                <line x1={lx + 12} y1={ly + titleH + 13} x2={lx + 44} y2={ly + titleH + 13}
                  stroke="#222" strokeWidth={1.5} />
                <LegendLabel
                  x={lx + 48} y={ly + titleH + 13} w={lw - 58}
                  value={labels.existing || t(tr.legendExisting)}
                  onSave={txt => setLegendLabel('existing', txt)}
                />

                {/* dashed line row */}
                <rect x={lx + 12} y={ly + titleH + 26} width={32} height={14} rx={2}
                  fill="none" stroke="#444" strokeWidth={1.2} strokeDasharray="5 3" />
                <line x1={lx + 12} y1={ly + titleH + 33} x2={lx + 44} y2={ly + titleH + 33}
                  stroke="#444" strokeWidth={1.5} strokeDasharray="5 3" />
                <LegendLabel
                  x={lx + 48} y={ly + titleH + 33} w={lw - 58}
                  value={labels.toAcquire || t(tr.legendToAcquire)}
                  onSave={txt => setLegendLabel('toAcquire', txt)}
                />

                {/* separator */}
                <line x1={lx + 10} y1={ly + titleH + 48} x2={lx + lw - 10} y2={ly + titleH + 48}
                  stroke="#ddd" strokeWidth={1} />

                {/* color label rows */}
                {colorRows.map(([key, hex, val], i) => (
                  <g key={key}>
                    <rect x={lx + 12} y={ly + titleH + 58 + i * 28} width={24} height={16} rx={3}
                      fill={hex} />
                    <LegendLabel
                      x={lx + 44}
                      y={ly + titleH + 66 + i * 28}
                      w={lw - 58}
                      value={val}
                      onSave={t => setLegendLabel(key, t)}
                    />
                  </g>
                ))}
              </g>
            )}
          </g>
        );
      })()}

      {/* ── Title box ── */}
      <TitleBox x={16} y={48} w={180} h={28} value={data.title} onSave={setTitle} />

      {/* ── Spine backbone — straight line through all dots + horizontal stub to the TitleBox ── */}
      {n > 0 && (() => {
        const first = spineAt(0);
        const last  = spineAt(n - 1);
        const slope = SPINE_DX / LEVEL_STEP;        // dx per unit dy along the spine
        const len   = Math.hypot(SPINE_DX, LEVEL_STEP);
        const ex    = SPINE_DX / len;
        const ey    = LEVEL_STEP / len;
        const botPad = 70;
        // connector leaves the middle of the TitleBox right edge (x=16,y=48,w=180,h=28)
        const titleX = 16 + 180;
        const titleY = 48 + 14;
        // x of the straight spine at the connector's y → meeting point
        const joinX = first.x + slope * (titleY - first.y);
        return (
          <>
            {/* straight spine through every dot, from the meeting point down past the last */}
            <line x1={joinX} y1={titleY} x2={last.x + ex * botPad} y2={last.y + ey * botPad}
              stroke="#29ABE2" strokeWidth={3} strokeLinecap="round" />
            {/* horizontal stub: TitleBox right-edge middle → spine */}
            <line x1={titleX} y1={titleY} x2={joinX} y2={titleY}
              stroke="#29ABE2" strokeWidth={3} strokeLinecap="round" />
          </>
        );
      })()}

      {/* ── Levels ── */}
      {data.levels.map((level, li) => {
        const sp = spineAt(li);
        const { nodes, segs } = layoutConcepts(level.concepts, sp.x, sp.y);
        const kBoxes = layoutKnowledge(level.knowledge, sp.y, knowX0);

        return (
          <g key={level.id}>
            {/* spine card label */}
            <SpineLabel spineX={sp.x} spineY={sp.y}
              value={(typeof level.spineLabel === 'string' ? level.spineLabel : level.spineLabel.title) || ''}
              onSave={t => updateSpineCard(li, { title: t })}
            />

            {/* spine dot */}
            <circle cx={sp.x} cy={sp.y} r={SPINE_R}
              fill={spineDragging?.li === li ? '#fff' : '#29ABE2'}
              stroke="#29ABE2" strokeWidth={1.5}
              style={{ cursor: spineDragging?.li === li ? 'grabbing' : 'grab' }}
              onMouseDown={e => { if (e.button === 0) startSpineDrag(e, li); }}
              {...longPress((x, y) => openSpineMenu(x, y, li))}
              onContextMenu={e => { e.preventDefault(); e.stopPropagation(); openSpineMenu(e.clientX, e.clientY, li); }} />

            {/* bus segments */}
            {segs.map((s, i) => (
              <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                stroke="#29ABE2" strokeWidth={1.5} />
            ))}

            {/* concept nodes */}
            {nodes.map(node => {
              const isBeingDragged = dragging?.id === node.id && dragging.started;
              const isDropChild    = dropHint?.kind === 'child' && dropHint.targetId === node.id;
              return (
                <g key={node.id}
                  onContextMenu={e => { e.preventDefault(); e.stopPropagation(); openNodeMenu(e.clientX, e.clientY, li, node.id, node.isTop); }}
                  onMouseDown={e => { if (e.button === 0) startDrag(e, node.id); }}
                  {...longPress((x, y) => openNodeMenu(x, y, li, node.id, node.isTop))}
                  style={{ cursor: isBeingDragged ? 'grabbing' : 'grab' }}
                >
                  {/* circle — click to collapse/expand if has children */}
                  {(() => {
                    const nodeColorHex = node.color ? NODE_COLORS[node.color] : null;
                    const circleFill = isDropChild
                      ? '#d9f0fb'
                      : nodeColorHex
                        ? nodeColorHex
                        : node.collapsed ? '#d9f0fb' : '#fff';
                    const circleStroke = isDropChild ? '#0d7ab5'
                      : nodeColorHex ? nodeColorHex : '#29ABE2';
                    const indicatorColor = nodeColorHex ? '#fff' : '#29ABE2';
                    return (
                      <>
                        <circle cx={node.x} cy={node.y} r={NODE_R}
                          fill={circleFill}
                          stroke={circleStroke}
                          strokeWidth={isDropChild ? 2.5 : 1.5}
                          opacity={isBeingDragged ? 0.25 : 1}
                          style={{ cursor: node.hasChildren ? 'pointer' : undefined }}
                          onClick={e => {
                            if (!dragging?.started && node.hasChildren) {
                              e.stopPropagation();
                              toggleCollapse(li, node.id);
                            }
                          }}
                        />
                        {/* collapse indicator */}
                        {node.hasChildren && !isBeingDragged && (
                          <text x={node.x} y={node.y} textAnchor="middle" dominantBaseline="middle"
                            fontSize={10} fill={indicatorColor} fontWeight="bold"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}>
                            {node.collapsed ? '▶' : '▼'}
                          </text>
                        )}
                      </>
                    );
                  })()}
                  {/* text + optional image to the right */}
                  {!isBeingDragged && (
                    <NodeLabel x={node.x + NODE_R + 7} y={node.y}
                      value={node.label}
                      imageUrl={node.imageUrl || ''}
                      onImageClick={url => setLightboxUrl(url)}
                      onSave={l => setNodeLabel(li, node.id, l)} />
                  )}
                </g>
              );
            })}

            {/* knowledge cards */}
            {kBoxes.map(box => {
              const isCardDragging = draggingCard?.id === box.id;
              const liveBox = isCardDragging
                ? { ...box, x: draggingCard.liveX, y: draggingCard.liveY }
                : box;
              return (
                <g key={box.id} {...longPress((x, y) => openKnowMenu(x, y, li, box.id))}>
                  <KnowledgeCardComponent box={liveBox}
                    onEdit={e => { e.stopPropagation(); setEditingCard({ li, id: box.id }); }}
                    onContextMenu={e => { e.preventDefault(); e.stopPropagation(); openKnowMenu(e.clientX, e.clientY, li, box.id); }}
                    onShowTooltip={(text, x, y) => setTooltip({ text, x, y })}
                    onHideTooltip={() => setTooltip(null)}
                    onCardDragStart={e => startCardDrag(e, box, li)}
                    onImageClick={url => setLightboxUrl(url)} />
                </g>
              );
            })}
          </g>
        );
      })}

      {/* ── Bottom legend ── */}
      {(() => {
        const ly = svgH - 52;
        return (
          <g>
            <line x1={SPINE_X0 + 10} y1={ly - 5} x2={SPINE_X0 + 10} y2={ly + 35} stroke="#ccc" strokeWidth={1} />
            <text x={90}  y={ly + 10} textAnchor="middle" fontSize={10} fontWeight="bold" fill="#444">{t(tr.domDesign)}</text>
            <text x={90}  y={ly + 24} textAnchor="middle" fontSize={9} fill="#888">{t(tr.domDesignSub)}</text>
            <text x={380} y={ly + 10} textAnchor="middle" fontSize={10} fontWeight="bold" fill="#444">{t(tr.conceptsLabel)}</text>
            <text x={380} y={ly + 24} textAnchor="middle" fontSize={9} fill="#888">{t(tr.conceptsSub)}</text>
            <text x={knowX0 + 145} y={ly + 10} textAnchor="middle" fontSize={10} fontWeight="bold" fill="#444">{t(tr.knowledgeLabel)}</text>
            <text x={knowX0 + 145} y={ly + 24} textAnchor="middle" fontSize={9} fill="#888">{t(tr.knowledgeSub)}</text>
          </g>
        );
      })()}

      {/* ── Add level button (bottom left) ── */}
      {(() => {
        const btnY = lastSp.y + KNOW_H - KNOW_HEADER_H / 2 + 16;
        return (<>
          <g onClick={addLevel} style={{ cursor: 'pointer' }}>
            <rect x={16} y={btnY} width={85} height={22} rx={4} fill="#29ABE2" />
            <text x={58} y={btnY + 13} textAnchor="middle" fontSize={11} fill="#fff" fontWeight="bold">{t(tr.addLevelBtn)}</text>
          </g>
          {n > 1 && (
            <g onClick={() => removeLevelAt(n - 1)} style={{ cursor: 'pointer' }}>
              <rect x={108} y={btnY} width={85} height={22} rx={4} fill="#f7f7f7" stroke="#ccc" strokeWidth={1} />
              <text x={150} y={btnY + 13} textAnchor="middle" fontSize={11} fill="#888">{t(tr.removeLevelBtn)}</text>
            </g>
          )}
        </>);
      })()}

      {/* ── Card drag ghost (semi-transparent outline) ── */}
      {draggingCard?.moved && (() => {
        const { liveX, liveY } = draggingCard;
        return (
          <g style={{ pointerEvents: 'none', opacity: 0.45 }}>
            <rect x={liveX + 2} y={liveY + 2} width={KNOW_W} height={KNOW_H} rx={6} fill="rgba(0,0,0,0.06)" />
            <rect x={liveX} y={liveY} width={KNOW_W} height={KNOW_H} rx={6}
              fill="#fff" stroke="#29ABE2" strokeWidth={1.5} strokeDasharray="5 3" />
            <rect x={liveX} y={liveY} width={KNOW_W} height={KNOW_HEADER_H + 6} rx={6} fill="#29ABE2" />
          </g>
        );
      })()}

      {/* ── Spine drag ghost ── */}
      {spineDragging?.started && (() => {
        const sp = spineAt(spineDragging.li);
        return (
          <g style={{ pointerEvents: 'none' }}>
            <circle cx={sp.x} cy={spineDragging.svgY} r={SPINE_R}
              fill="#29ABE2" opacity={0.7} />
          </g>
        );
      })()}

      {/* ── Spine drop hint: horizontal line ── */}
      {spineDropHint && spineDragging?.started && (() => {
        const sp0 = spineAt(0);
        return (
          <line
            x1={sp0.x - 60} y1={spineDropHint.lineY}
            x2={sp0.x + 80} y2={spineDropHint.lineY}
            stroke="#29ABE2" strokeWidth={2} strokeDasharray="6 3"
            style={{ pointerEvents: 'none' }}
          />
        );
      })()}

      {/* ── Drag ghost ── */}
      {dragging?.started && (() => {
        const src = allNodes.find(n => n.id === dragging.id);
        return (
          <g style={{ pointerEvents: 'none' }}>
            <circle cx={dragging.svgX} cy={dragging.svgY} r={NODE_R}
              fill="#fff" stroke="#29ABE2" strokeWidth={1.5} opacity={0.75} />
            {src && (
              <text x={dragging.svgX + NODE_R + 6} y={dragging.svgY}
                textAnchor="start" dominantBaseline="middle"
                fontSize={13} fill="#555" opacity={0.75}
                style={{ userSelect: 'none' }}>
                {src.label || '…'}
              </text>
            )}
          </g>
        );
      })()}

      {/* ── Drop hint: reorder line ── */}
      {dropHint?.kind === 'reorder' && (
        <line
          x1={dropHint.lineX!} y1={dropHint.lineY! - NODE_R - 8}
          x2={dropHint.lineX!} y2={dropHint.lineY! + NODE_R + 8}
          stroke="#29ABE2" strokeWidth={2.5} strokeLinecap="round"
          style={{ pointerEvents: 'none' }}
        />
      )}
    </svg>

    {/* Context menu overlay */}
    <CtxMenu menu={ctxMenu} onClose={() => setCtxMenu(null)} />

    {/* Tooltip overlay — always on top, outside SVG */}
    {tooltip && (
      <div style={{
        position: 'fixed',
        left: tooltip.x + 14,
        top: tooltip.y - 10,
        zIndex: 99999,
        background: '#222',
        color: '#fff',
        fontSize: 11,
        lineHeight: '1.5',
        padding: '8px 11px',
        borderRadius: 7,
        maxWidth: 240,
        boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
        pointerEvents: 'none',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
      }}>
        {tooltip.text}
      </div>
    )}

    {/* Spine card editor modal */}
    {editingSpineCard !== null && (() => {
      const card = toSpineCard(data.levels[editingSpineCard]?.spineLabel ?? '');
      return (
        <KnowledgeCardEditor
          card={card}
          onSave={updates => { updateSpineCard(editingSpineCard, updates); setEditingSpineCard(null); }}
          onClose={() => setEditingSpineCard(null)}
        />
      );
    })()}

    {/* Knowledge card editor modal */}
    {editingCard && (() => {
      const lv  = data.levels[editingCard.li];
      const inExisting = (lv?.knowledge.existing ?? []).some(k => k.id === editingCard.id);
      const raw = [...(lv?.knowledge.existing ?? []), ...(lv?.knowledge.toAcquire ?? [])]
                    .find(k => k.id === editingCard.id);
      if (!raw) return null;
      // effective border style: card override, else section default (existing = solid, to-acquire = dashed)
      const effective = { ...raw, dashed: raw.dashed ?? !inExisting };
      return (
        <KnowledgeCardEditor
          card={effective}
          showBorderStyle
          onSave={updates => {
            updateKNode(editingCard.li, editingCard.id, updates);
            setEditingCard(null);
          }}
          onClose={() => setEditingCard(null)}
        />
      );
    })()}

    {/* ── Node image picker modal ── */}
    {imageModal && createPortal(
      <NodeImageModal
        modal={imageModal}
        onConfirm={url => { setNodeImage(imageModal.li, imageModal.nodeId, url); setImageModal(null); }}
        onClose={() => setImageModal(null)}
      />,
      document.body
    )}

    {/* ── Lightbox ── */}
    {lightboxUrl && createPortal(
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.82)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={() => setLightboxUrl(null)}
      >
        <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
          onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setLightboxUrl(null)}
            style={{
              position: 'absolute', top: -14, right: -14,
              width: 32, height: 32, borderRadius: '50%',
              border: 'none', background: '#fff', color: '#222',
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
              zIndex: 1,
            }}>✕</button>
          <img
            src={lightboxUrl} alt=""
            style={{
              maxWidth: '90vw', maxHeight: '90vh',
              borderRadius: 10,
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              display: 'block',
            }}
          />
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

// ── Zoom button ───────────────────────────────────────────────────────────────
const ZoomBtn: React.FC<ZoomBtnProps> = ({ label, onClick, wide }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        minWidth: wide ? 54 : 32, height: 30, border: '1px solid #d0e8f5',
        borderRadius: 6, background: hov ? '#e8f5fb' : '#fff',
        color: '#29ABE2', fontWeight: 'bold', fontSize: 15,
        cursor: 'pointer', outline: 'none', transition: 'background .12s',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 6px', fontFamily: 'inherit',
      }}
    >{label}</button>
  );
};

// ── Infinite canvas wrapper ───────────────────────────────────────────────────
const ConceptMapInstance: React.FC<ConceptMapInstanceProps> = ({ project, onInstanceReady }) => {
  const saved = (project as any).jsonData?.mindmap;
  const [data, setData]           = useState<MindmapData>(() => (saved?.levels ? saved : makeDefaultData()));
  const [tf, setTf]               = useState<TransformState>({ x: 40, y: 40, scale: 1 });
  const [panning, setPanning]     = useState(false);
  const containerRef     = useRef<HTMLDivElement>(null);
  const lastMouse        = useRef({ x: 0, y: 0 });
  const panStart         = useRef<{ x: number; y: number } | null>(null);
  const didPan           = useRef(false);
  const lastPinchDist    = useRef<number | null>(null);
  const dataRef       = useRef<MindmapData>(data);
  const svgElRef      = useRef<SVGElement | null>(null);

  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { onInstanceReady({ getData: () => dataRef.current as any, svgElRef }); }, [onInstanceReady]);

  // ── Autosave to local storage (IndexedDB) / backend ──────────────────────
  // Matches WhiteboardCanvas / TextDocCanvas: every edit flows through setData,
  // so a debounced save on `data` change persists the mindmap automatically.
  // Without this, free-tier edits live only in component state (the manual Save
  // button is hidden in free mode) and are lost on reload or project switch.
  const { schedule: scheduleSave } = useAutosave<MindmapData>(
    d => { if (project?.id) return patchProject(project.id, { jsonData: { mindmap: d } } as any); },
  );
  // Skip the initial mount: `data` is seeded from the project's saved snapshot,
  // so the first run isn't a user edit and shouldn't trigger a write.
  const didMountSave = useRef(false);
  useEffect(() => {
    if (!didMountSave.current) { didMountSave.current = true; return; }
    scheduleSave(data);
  }, [data, scheduleSave]);

  // ── wheel → zoom around cursor ──────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect   = el.getBoundingClientRect();
      const mx     = e.clientX - rect.left;
      const my     = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      setTf(t => {
        const ns = Math.max(0.1, Math.min(4, t.scale * factor));
        return { scale: ns, x: mx - (mx - t.x) * (ns / t.scale), y: my - (my - t.y) * (ns / t.scale) };
      });
    };
    el.addEventListener('wheel',      onWheel,  { passive: false });
    el.addEventListener('touchmove',  e => e.preventDefault(), { passive: false });
    return () => { el.removeEventListener('wheel', onWheel); };
  }, []);

  // ── mouse → pan ──────────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    const tag = (e.target as HTMLElement).tagName.toLowerCase();
    if (['input', 'textarea', 'button'].includes(tag)) return;
    panStart.current  = { x: e.clientX, y: e.clientY };
    lastMouse.current = { x: e.clientX, y: e.clientY };
    didPan.current    = false;
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!panStart.current) return;
    const dist = Math.hypot(e.clientX - panStart.current.x, e.clientY - panStart.current.y);
    if (dist > 4) { didPan.current = true; setPanning(true); }
    if (!didPan.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setTf(t => ({ ...t, x: t.x + dx, y: t.y + dy }));
  };

  const onMouseUp = () => { panStart.current = null; setPanning(false); };

  // ── touch → pan (1 finger) + pinch-zoom (2 fingers) ─────────────────────
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      panStart.current  = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
      lastMouse.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
      didPan.current    = false;
      lastPinchDist.current = null;
    } else if (e.touches.length === 2) {
      panStart.current = null;
      const dx = e.touches[0]!.clientX - e.touches[1]!.clientX;
      const dy = e.touches[0]!.clientY - e.touches[1]!.clientY;
      lastPinchDist.current = Math.hypot(dx, dy);
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 1 && panStart.current) {
      const dist = Math.hypot(
        e.touches[0]!.clientX - panStart.current.x,
        e.touches[0]!.clientY - panStart.current.y
      );
      if (dist > 6) { didPan.current = true; setPanning(true); }
      if (!didPan.current) return;
      const dx = e.touches[0]!.clientX - lastMouse.current.x;
      const dy = e.touches[0]!.clientY - lastMouse.current.y;
      lastMouse.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
      setTf(t => ({ ...t, x: t.x + dx, y: t.y + dy }));
    } else if (e.touches.length === 2 && lastPinchDist.current !== null) {
      const dx = e.touches[0]!.clientX - e.touches[1]!.clientX;
      const dy = e.touches[0]!.clientY - e.touches[1]!.clientY;
      const dist   = Math.hypot(dx, dy);
      const factor = dist / lastPinchDist.current;
      const rect   = containerRef.current!.getBoundingClientRect();
      const cx = (e.touches[0]!.clientX + e.touches[1]!.clientX) / 2 - rect.left;
      const cy = (e.touches[0]!.clientY + e.touches[1]!.clientY) / 2 - rect.top;
      setTf(t => {
        const ns = Math.max(0.1, Math.min(4, t.scale * factor));
        return { scale: ns, x: cx - (cx - t.x) * (ns / t.scale), y: cy - (cy - t.y) * (ns / t.scale) };
      });
      lastPinchDist.current = dist;
    }
  };

  const onTouchEnd = () => { panStart.current = null; lastPinchDist.current = null; setPanning(false); };

  // ── zoom buttons ─────────────────────────────────────────────────────────
  const zoomIn    = () => setTf(t => ({ ...t, scale: Math.min(t.scale * 1.2, 4) }));
  const zoomOut   = () => setTf(t => ({ ...t, scale: Math.max(t.scale / 1.2, 0.1) }));
  const zoomReset = () => setTf({ x: 40, y: 40, scale: 1 });

  return (
    <div ref={containerRef}
      style={{ width: '100%', height: '100%', overflow: 'hidden',
        position: 'relative', background: '#f4f6f8',
        cursor: panning ? 'grabbing' : 'default',
      }}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}    onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
    >
      {/* ── infinite canvas layer ── */}
      <div style={{
        position: 'absolute', top: 0, left: 0,
        transformOrigin: '0 0',
        transform: `translate(${tf.x}px,${tf.y}px) scale(${tf.scale})`,
        userSelect: panning ? 'none' : 'auto',
      }}>
        <ConceptMapDiagram data={data} onChange={setData} svgElRef={svgElRef} />
      </div>

      {/* ── zoom controls (bottom-right) ── */}
      <div style={{
        position: 'absolute', bottom: 18, right: 18, zIndex: 30,
        display: 'flex', alignItems: 'center', gap: 4,
        background: 'rgba(255,255,255,0.97)',
        borderRadius: 10, padding: '5px 7px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.11)',
        border: '1px solid #d8eef8',
      }}>
        <ZoomBtn label="−" onClick={zoomOut} />
        <ZoomBtn label={`${Math.round(tf.scale * 100)}%`} onClick={zoomReset} wide />
        <ZoomBtn label="+" onClick={zoomIn} />
      </div>
    </div>
  );
};

// ── Export ────────────────────────────────────────────────────────────────────
const MindmapCanvas: React.FC<MindmapCanvasProps> = ({ project, onInstanceReady }) => {
  if (!project) return (
    <div className="mindmap-canvas-root" style={{ flex: 1, display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: '#aaa', fontSize: 16 }}>
      Double-cliquez sur un projet pour l'ouvrir
    </div>
  );
  return (
    <div className="mindmap-canvas-root" style={{ flex: 1, width: 0, minWidth: 0, height: '100vh', overflow: 'hidden' }}>
      <ConceptMapInstance key={project?.id} project={project} onInstanceReady={onInstanceReady} />
    </div>
  );
};

export default MindmapCanvas;
